import { computed, WritableSignal } from '@angular/core';
import {
  ProjectedSignal,
  ProjectedSignalOptions,
} from './projected-signal.types';
import { SIGNAL } from '@angular/core/primitives/signals';

// Re-export types for external use
export type { ProjectedSignal, ProjectedSignalOptions };

/**
 * Creates a projected signal that bridges Angular Signal Forms with NgRx SignalStore.
 *
 * ## Problem
 * Angular Signal Forms require a `WritableSignal<T>` for two-way binding via the `[field]` directive.
 * NgRx SignalStore exposes state as readonly signals with dedicated methods for updates.
 * This utility creates a proxy signal that reads from a computation and writes via a custom updater.
 *
 * ## How It Works
 * - **Reading**: Wraps the computation in a `computed()` signal, staying reactive to store changes
 * - **Writing**: Delegates `set()` and `update()` calls to the provided update function
 * - **No internal state**: Acts purely as a proxy, the store remains the single source of truth
 *
 * ## Usage with Signal Forms
 *
 * ```typescript
 * // In your component
 * readonly store = inject(MyStore);
 *
 * // Create a projected signal that bridges store ↔ form
 * readonly formData = projectedSignal({
 *   computation: () => this.store.formData(),
 *   update: (value) => this.store.updateFormData(value)
 * });
 *
 * // Create the signal form
 * readonly form = form(this.formData);
 * ```
 *
 * ```html
 * <!-- In your template -->
 * <input [formField]="form.name" />
 * <input [formField]="form.email" />
 * ```
 *
 * ## Data Flow
 * 1. Store state changes → `computation()` re-evaluates → form reflects new value
 * 2. User types in form → `[field]` directive calls `set()` → `update()` dispatches to store
 *
 * @template T - The type of value managed by the signal
 * @param options - Configuration with computation (read) and update (write) functions
 * @returns A `ProjectedSignal<T>` compatible with Angular Signal Forms
 *
 * @see https://medium.com/@kobihari/connecting-signal-forms-to-a-signal-store-39dabd69b888
 * @see https://angular.dev/essentials/signal-forms
 *
 * @example
 * // Simple usage with a store slice
 * readonly userName = projectedSignal({
 *   computation: () => this.store.user().name,
 *   update: (name) => this.store.updateUser({ name })
 * });
 *
 * @example
 * // With form validation in Signal Forms
 * readonly emailField = projectedSignal({
 *   computation: () => this.store.email(),
 *   update: (value) => this.store.setEmail(value)
 * });
 * readonly emailForm = form(this.emailField, { validators: [required(), email()] });
 */
export function projectedSignal<T>(
  options: ProjectedSignalOptions<T>
): ProjectedSignal<T> {
  const internalSignal = computed(options.computation);

  const res: ProjectedSignal<T> = Object.assign(() => internalSignal(), {
    [SIGNAL]: internalSignal[SIGNAL],
    set: (value: T) => options.update(value),
    update: (updateFn: (value: T) => T) => {
      const newValue = updateFn(internalSignal());
      options.update(newValue);
    },
    asReadonly: () => internalSignal,
  } as WritableSignal<T>);
  return res;
}
